import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHome, FaNewspaper, FaArchive, FaBrain, FaDollarSign } from 'react-icons/fa';
import { PAPER_SOURCE, SOURCE_NAME } from '../constants/tagConstants';

const navItems = [
  { name: 'Home', path: '/', icon: FaHome },
  { name: 'Today', path: '/today', icon: FaNewspaper },
  { name: 'Archive', path: '/archive', icon: FaArchive },
  { name: 'AI', path: '/ai', icon: FaBrain },
  { name: 'Pricing', path: '/pricing', icon: FaDollarSign },
];

const SOURCES = [
  { key: 'arxiv', name: 'arXiv', color: 'bg-red-400', activeText: 'text-red-300', activeBorder: 'border-red-400/50', activeBg: 'bg-red-500/15' },
  { key: 'biorxiv', name: 'bioRxiv', color: 'bg-green-400', activeText: 'text-green-300', activeBorder: 'border-green-400/50', activeBg: 'bg-green-500/15' },
  { key: 'psyarxiv', name: 'psyArXiv', color: 'bg-blue-400', activeText: 'text-blue-300', activeBorder: 'border-blue-400/50', activeBg: 'bg-blue-500/15' },
];

function sourceUrl(source) {
  if (typeof window === 'undefined') return '#';
  const { protocol, hostname, port, pathname, search } = window.location;
  const path = pathname + search;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portMap = { arxiv: '4000', biorxiv: '4010', psyarxiv: '4020' };
    return `${protocol}//${hostname}:${portMap[source] || port}${path}`;
  }

  // Production / staging: swap the subdomain, preserving dev. prefix
  const isStaging = hostname.startsWith('dev.');
  const baseDomain = hostname.replace(/^(dev\.)?(bio\.|psy\.)?/, '');
  const sourcePrefix = { arxiv: '', biorxiv: 'bio.', psyarxiv: 'psy.' }[source] || '';
  const devPrefix = isStaging ? 'dev.' : '';
  return `${protocol}//${devPrefix}${sourcePrefix}${baseDomain}${path}`;
}

function MobileNav() {
  const location = useLocation();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden glass-nav border-t border-white/10 safe-area-bottom">
      {/* Source switcher strip */}
      <div className="flex items-center justify-center gap-1.5 px-3 pt-1.5 pb-0.5">
        {SOURCES.map((source) => {
          const isCurrent = source.key === PAPER_SOURCE;
          return (
            <a
              key={source.key}
              href={isCurrent ? undefined : sourceUrl(source.key)}
              className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide transition-all duration-200 border ${
                isCurrent
                  ? `${source.activeBg} ${source.activeBorder} ${source.activeText}`
                  : 'border-white/10 bg-white/5 text-white/30 hover:text-white/50'
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${source.color} ${isCurrent ? '' : 'opacity-40'}`} />
              {source.name}
            </a>
          );
        })}
      </div>

      {/* Navigation items */}
      <div className="flex items-center justify-around h-14 px-1">
        {navItems.map((item) => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname === item.path || location.pathname.startsWith(item.path + '/');
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.path}
              className="relative flex flex-col items-center justify-center flex-1 py-2"
            >
              {isActive && (
                <motion.div
                  className="absolute -top-px left-1/2 -translate-x-1/2 w-8 h-0.5 bg-yellow-300 rounded-full"
                  layoutId="mobileActiveTab"
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <Icon className={`text-base mb-1 transition-colors duration-200 ${
                isActive ? 'text-yellow-300' : 'text-white/35'
              }`} />
              <span className={`text-[9px] font-semibold tracking-wide transition-colors duration-200 ${
                isActive ? 'text-yellow-300' : 'text-white/35'
              }`}>
                {item.name}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default MobileNav;
